import React, { useState, useEffect } from 'react';
import { Row, Col, Spin, Alert, Tooltip, Progress, Tag, Button, message } from 'antd';
import {
  CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled,
  InfoCircleOutlined, GlobalOutlined, ReloadOutlined, CopyOutlined,
  CaretDownOutlined, CaretRightOutlined, ExportOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const authH = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

/* ── Copy-to-clipboard helper ── */
const copyText = (text) => {
  navigator.clipboard.writeText(text).then(() => message.success('Copied to clipboard'));
};

/* ── Score ring ── */
const ScoreRing = ({ score }) => {
  const r     = 54;
  const circ  = 2 * Math.PI * r;
  const dash  = (score / 100) * circ;
  const color = score >= 80 ? 'var(--ok)' : score >= 60 ? 'oklch(0.50 0.14 75)' : 'var(--danger)';
  const label = score >= 80 ? 'Excellent' : score >= 60 ? 'Fair' : 'Poor';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ position: 'relative', width: 140, height: 140 }}>
        <svg width={140} height={140} style={{ transform: 'rotate(-90deg)' }}>
          <circle cx={70} cy={70} r={r} fill="none" stroke="var(--border)" strokeWidth={10} />
          <circle
            cx={70} cy={70} r={r} fill="none" stroke={color} strokeWidth={10}
            strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
            style={{ transition: 'stroke-dasharray 0.6s ease' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 32, fontWeight: 700, color, letterSpacing: '-0.04em' }}>{score}</span>
          <span style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 500 }}>/ 100</span>
        </div>
      </div>
      <div style={{
        padding: '3px 12px', borderRadius: 999, fontWeight: 600, fontSize: 12,
        background: score >= 80 ? 'var(--ok-bg)' : score >= 60 ? 'var(--warn-bg)' : 'var(--danger-bg)',
        color,
      }}>
        {label}
      </div>
    </div>
  );
};

/* ── Stat tile ── */
const StatTile = ({ label, value, unit, tone }) => {
  const c = {
    ok:      { fg: 'var(--ok)',           bg: 'var(--ok-bg)'     },
    warn:    { fg: 'oklch(0.45 0.14 75)', bg: 'var(--warn-bg)'   },
    danger:  { fg: 'var(--danger)',       bg: 'var(--danger-bg)' },
    neutral: { fg: 'var(--text-2)',       bg: 'var(--surface-2)' },
  }[tone || 'neutral'];
  return (
    <div style={{ borderRadius: 'var(--r-lg)', border: '1px solid var(--border)', background: c.bg, padding: '18px 20px' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        {label}
      </div>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 26, fontWeight: 700, color: c.fg, letterSpacing: '-0.04em', lineHeight: 1 }}>
        {value}{unit && <span style={{ fontSize: 13, fontWeight: 500, marginLeft: 4 }}>{unit}</span>}
      </div>
    </div>
  );
};

/* ── DNS verify button + result ── */
const DnsVerifyButton = ({ type, domain, tokens, onVerified }) => {
  const [status,  setStatus]  = useState(null);  // null | 'checking' | 'found' | 'not_found' | 'error'
  const [details, setDetails] = useState(null);

  const check = async () => {
    setStatus('checking');
    setDetails(null);
    try {
      const body = { type, domain };
      if (type === 'dkim' && tokens) body.tokens = tokens;
      const res = await axios.post('/api/settings/check-dns', body, { headers: authH() });
      const found = res.data.found;
      setStatus(found ? 'found' : 'not_found');
      setDetails(res.data);
      if (found && onVerified) onVerified();
    } catch (err) {
      setStatus('error');
      setDetails({ message: err.response?.data?.message || 'DNS lookup failed' });
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      <Button
        size="small"
        icon={status === 'checking' ? null : status === 'found' ? <CheckCircleFilled style={{ color: 'var(--ok)' }} /> : <InfoCircleOutlined />}
        loading={status === 'checking'}
        onClick={check}
        style={{ borderRadius: 6, fontWeight: 500 }}
      >
        {status === 'checking' ? 'Checking DNS…' : status === 'found' ? 'Record found — verified!' : status === 'not_found' ? 'Not found — recheck' : status === 'error' ? 'Check failed — retry' : 'Verify DNS Record'}
      </Button>

      {status === 'found' && (
        <div style={{
          marginTop: 10, padding: '10px 14px', borderRadius: 'var(--r-md)',
          background: 'var(--ok-bg)', border: '1px solid color-mix(in srgb, var(--ok) 30%, transparent)',
        }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'var(--ok)', marginBottom: 4 }}>
            ✓ Record detected in DNS
          </div>
          {type === 'dkim' && details?.results && (
            <div>
              {details.results.map(r => (
                <div key={r.host} style={{ fontSize: 11.5, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 3 }}>
                  {r.found ? '✓' : '✗'} {r.host}
                </div>
              ))}
            </div>
          )}
          {(type === 'spf' || type === 'dmarc') && details?.record && (
            <div style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', marginTop: 2, wordBreak: 'break-all' }}>
              {details.record}
            </div>
          )}
        </div>
      )}

      {status === 'not_found' && (
        <div style={{
          marginTop: 10, padding: '10px 14px', borderRadius: 'var(--r-md)',
          background: 'var(--warn-bg)', border: '1px solid color-mix(in srgb, var(--warn) 30%, transparent)',
        }}>
          <div style={{ fontWeight: 600, fontSize: 12.5, color: 'oklch(0.45 0.14 75)', marginBottom: 4 }}>
            Record not detected yet
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
            DNS changes can take up to 48 hours to propagate. If you just added the record, wait a few minutes and try again.
          </div>
          {type === 'dkim' && details?.results && (
            <div style={{ marginTop: 6 }}>
              {details.results.map(r => (
                <div key={r.host} style={{ fontSize: 11.5, fontFamily: 'var(--font-mono)', color: 'var(--text-2)', marginTop: 2 }}>
                  {r.found ? '✓' : '✗'} {r.host}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {status === 'error' && (
        <div style={{
          marginTop: 10, padding: '10px 14px', borderRadius: 'var(--r-md)',
          background: 'var(--danger-bg)', border: '1px solid color-mix(in srgb, var(--danger) 30%, transparent)',
          fontSize: 12, color: 'var(--danger)',
        }}>
          {details?.message || 'Could not complete DNS lookup. Check your connection and try again.'}
        </div>
      )}
    </div>
  );
};

/* ── DNS record table ── */
const DnsRecord = ({ type, host, value }) => {
  const col = { fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.04em', padding: '7px 12px', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' };
  const cell = { padding: '9px 12px', fontFamily: 'var(--font-mono)', fontSize: 12, wordBreak: 'break-all', verticalAlign: 'middle' };
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 'var(--r-md)', overflow: 'hidden', marginBottom: 10 }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...col, width: 60 }}>Type</th>
            <th style={{ ...col }}>Host / Name</th>
            <th style={{ ...col }}>Value</th>
            <th style={{ ...col, width: 70, textAlign: 'center' }}>Copy</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ background: 'var(--surface)' }}>
            <td style={{ ...cell, textAlign: 'center' }}>
              <Tag style={{ fontSize: 11, borderRadius: 4, margin: 0, fontFamily: 'var(--font-mono)' }}>{type}</Tag>
            </td>
            <td style={{ ...cell, color: 'var(--brand-primary)' }}>{host}</td>
            <td style={{ ...cell, color: 'var(--text-2)' }}>{value}</td>
            <td style={{ ...cell, textAlign: 'center' }}>
              <Button
                size="small" icon={<CopyOutlined />}
                onClick={() => copyText(value)}
                style={{ borderRadius: 6, fontSize: 11 }}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

/* ── Auth card — expandable with instructions ── */
const AuthCard = ({ label, passed, summary, children }) => {
  const [open, setOpen] = useState(false);
  const statusColor = passed === true ? 'var(--ok)' : passed === false ? 'var(--danger)' : 'oklch(0.50 0.14 75)';
  const statusBg    = passed === true ? 'var(--ok-bg)' : passed === false ? 'var(--danger-bg)' : 'var(--warn-bg)';
  const statusLabel = passed === true ? 'Pass' : passed === false ? 'Fail' : 'Pending';

  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      {/* Header row */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '13px 0', cursor: 'pointer', userSelect: 'none' }}
      >
        <div style={{ flexShrink: 0 }}>
          {passed === true  && <CheckCircleFilled  style={{ color: 'var(--ok)',     fontSize: 18 }} />}
          {passed === false && <CloseCircleFilled  style={{ color: 'var(--danger)', fontSize: 18 }} />}
          {passed === null  && <ExclamationCircleFilled style={{ color: 'oklch(0.50 0.14 75)', fontSize: 18 }} />}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 650, fontSize: 13.5, color: 'var(--text)' }}>{label}</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>{summary}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            padding: '2px 8px', borderRadius: 999, fontSize: 11.5, fontWeight: 600,
            background: statusBg, color: statusColor,
          }}>
            {statusLabel}
          </div>
          {open
            ? <CaretDownOutlined  style={{ fontSize: 11, color: 'var(--text-3)' }} />
            : <CaretRightOutlined style={{ fontSize: 11, color: 'var(--text-3)' }} />
          }
        </div>
      </div>

      {/* Expandable instructions */}
      {open && (
        <div style={{
          paddingBottom: 16, paddingLeft: 30,
          animation: 'fadeIn 0.15s ease',
        }}>
          {children}
        </div>
      )}
    </div>
  );
};

/* ── Recommendation item ── */
const Rec = ({ icon, title, body, tone }) => {
  const c = {
    danger: { bg: 'var(--danger-bg)', fg: 'var(--danger)' },
    warn:   { bg: 'var(--warn-bg)',   fg: 'oklch(0.45 0.14 75)' },
    ok:     { bg: 'var(--ok-bg)',     fg: 'var(--ok)' },
  }[tone || 'warn'];
  return (
    <div style={{
      display: 'flex', gap: 12, padding: '14px 16px',
      background: c.bg, borderLeft: `3px solid ${c.fg}`,
      borderRadius: '0 var(--r-md) var(--r-md) 0', marginBottom: 10,
    }}>
      <span style={{ color: c.fg, fontSize: 16, flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div>
        <div style={{ fontWeight: 600, fontSize: 13.5, color: 'var(--text)', marginBottom: 3 }}>{title}</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>{body}</div>
      </div>
    </div>
  );
};

/* ── Section card wrapper ── */
const Section = ({ title, tooltip, children }) => (
  <div style={{
    borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
    background: 'var(--surface)', boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
  }}>
    <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ fontWeight: 650, fontSize: 14, color: 'var(--text)' }}>{title}</span>
      {tooltip && (
        <Tooltip title={tooltip}>
          <InfoCircleOutlined style={{ fontSize: 12, color: 'var(--text-3)', cursor: 'help' }} />
        </Tooltip>
      )}
    </div>
    <div style={{ padding: '0 20px 4px' }}>{children}</div>
  </div>
);

/* ── Step label ── */
const Step = ({ n, text }) => (
  <div style={{ display: 'flex', gap: 10, marginBottom: 8, alignItems: 'flex-start' }}>
    <div style={{
      width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
      background: 'var(--brand-tint)', color: 'var(--brand-primary)',
      fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>{n}</div>
    <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, paddingTop: 1 }}>{text}</div>
  </div>
);

/* ── Main page ── */
const Deliverability = () => {
  const navigate  = useNavigate();
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState(null);
  const [sesStatus,  setSesStatus]  = useState(null);
  const [fromEmails, setFromEmails] = useState([]);
  // DMARC can't be read from AWS SES — persist the user's manual DNS check result
  const [dmarcPassed, setDmarcPassed] = useState(() => localStorage.getItem('dmarc_verified') === '1');

  const markDmarcVerified = () => {
    localStorage.setItem('dmarc_verified', '1');
    setDmarcPassed(true);
  };

  const load = () => {
    setLoading(true);
    setError(null);
    Promise.allSettled([
      axios.get('/api/settings/ses-status',      { headers: authH() }),
      axios.get('/api/settings/ses/from-emails', { headers: authH() }),
    ]).then(([sesRes, emailsRes]) => {
      if (sesRes.status === 'fulfilled')    setSesStatus(sesRes.value.data);
      else setError('Failed to load SES status');
      if (emailsRes.status === 'fulfilled') setFromEmails(emailsRes.value.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
      <Spin size="large" />
    </div>
  );

  /* ── Derived values ── */
  const configured    = sesStatus?.configured    ?? false;
  const bounceRate    = sesStatus?.bounceRate    ?? 0;
  const complaintRate = sesStatus?.complaintRate ?? 0;
  const spf           = sesStatus?.spf           ?? null;
  const dkim          = sesStatus?.dkim          ?? null;
  const dkimTokens    = sesStatus?.dkimTokens    ?? [];
  const domain        = sesStatus?.domain        ?? (sesStatus?.fromEmail?.split('@')[1] || 'yourdomain.com');

  /* Score */
  let score = 100;
  if (!configured)              score -= 30;
  if (bounceRate    > 0.10)     score -= 20;
  else if (bounceRate > 0.05)   score -= 10;
  if (complaintRate > 0.001)    score -= 20;
  else if (complaintRate > 0.0005) score -= 10;
  if (spf  === false)           score -= 10;
  if (dkim === false)           score -= 10;
  score = Math.max(0, Math.min(100, score));

  const bounceColor    = bounceRate    > 0.10 ? 'danger' : bounceRate    > 0.05 ? 'warn' : 'ok';
  const complaintColor = complaintRate > 0.001 ? 'danger' : complaintRate > 0.0005 ? 'warn' : 'ok';

  /* Inbox placement estimate */
  const placement = [
    { provider: 'Gmail',   pct: Math.min(99, Math.max(40, Math.round(score * 0.96))) },
    { provider: 'Outlook', pct: Math.min(99, Math.max(38, Math.round(score * 0.93))) },
    { provider: 'Yahoo',   pct: Math.min(99, Math.max(40, Math.round(score * 0.94))) },
    { provider: 'Apple',   pct: Math.min(99, Math.max(42, Math.round(score * 0.98))) },
  ];

  /* Recommendations */
  const recs = [];
  if (!configured) {
    recs.push({ tone: 'danger', icon: <ExclamationCircleFilled />, title: 'Connect AWS SES', body: 'Configure your AWS SES credentials in Settings → SES Connection before sending campaigns.' });
  }
  if (dkim === false) {
    recs.push({ tone: 'danger', icon: <CloseCircleFilled />, title: 'Enable DKIM signing', body: 'DKIM is not verified. Click "DKIM" in the Email Authentication section below for step-by-step DNS instructions.' });
  }
  if (spf === false) {
    recs.push({ tone: 'warn', icon: <ExclamationCircleFilled />, title: 'Add SPF record', body: 'Your sending identity is not verified. Click "SPF" below to see the exact DNS record to add.' });
  }
  recs.push({ tone: 'warn', icon: <InfoCircleOutlined />, title: 'Set up DMARC', body: 'Add a DMARC DNS record — required by Gmail & Yahoo for bulk senders. Click "DMARC" below for the exact record. Skip if already configured.' });
  if (bounceRate > 0.05) {
    recs.push({ tone: bounceRate > 0.10 ? 'danger' : 'warn', icon: <ExclamationCircleFilled />, title: 'High bounce rate', body: `Your bounce rate is ${(bounceRate * 100).toFixed(2)}% (good: <5%). Clean your list and remove addresses that have bounced.` });
  }
  if (complaintRate > 0.0005) {
    recs.push({ tone: complaintRate > 0.001 ? 'danger' : 'warn', icon: <ExclamationCircleFilled />, title: 'Elevated complaint rate', body: `Your complaint rate is ${(complaintRate * 100).toFixed(3)}% (good: <0.05%). Add an easy unsubscribe link and review sending frequency.` });
  }
  if (recs.length === 1 && recs[0].tone === 'warn' && recs[0].title === 'Set up DMARC') {
    recs.push({ tone: 'ok', icon: <CheckCircleFilled />, title: 'Looking good!', body: 'Your sender reputation is healthy. Keep maintaining clean lists and proper authentication.' });
  }

  return (
    <div>
      {/* Refresh */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
        <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 'var(--r-md)' }}>Refresh</Button>
      </div>

      {error && <Alert message={error} type="error" showIcon closable onClose={() => setError(null)} style={{ marginBottom: 20, borderRadius: 'var(--r-md)' }} />}

      {!configured && (
        <Alert
          type="warning" showIcon
          message="SES not connected"
          description="Connect your AWS SES credentials to see live deliverability data."
          action={<Button size="small" onClick={() => navigate('/settings#ses')}>Go to SES Settings</Button>}
          style={{ marginBottom: 20, borderRadius: 'var(--r-md)' }}
        />
      )}

      {/* Row 1 — Score + stat tiles */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={6}>
          <div style={{
            borderRadius: 'var(--r-lg)', border: '1px solid var(--border)',
            background: 'var(--surface)', boxShadow: 'var(--shadow-sm)',
            padding: '28px 20px',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16, height: '100%',
          }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.05em', alignSelf: 'flex-start' }}>
              Sender Score
            </div>
            <ScoreRing score={score} />
            <div style={{ fontSize: 11.5, color: 'var(--text-3)', textAlign: 'center', lineHeight: 1.5 }}>
              Based on bounce rate, complaint rate, and authentication status
            </div>
          </div>
        </Col>
        <Col xs={24} md={18}>
          <Row gutter={[12, 12]}>
            <Col xs={12} md={12}><StatTile label="Bounce Rate"    value={(bounceRate * 100).toFixed(2)}    unit="%" tone={bounceColor}    /></Col>
            <Col xs={12} md={12}><StatTile label="Complaint Rate" value={(complaintRate * 100).toFixed(3)} unit="%" tone={complaintColor} /></Col>
            <Col xs={12} md={12}><StatTile label="SES Status"  value={configured ? 'Active' : 'Not set'} tone={configured ? 'ok' : 'danger'} /></Col>
            <Col xs={12} md={12}><StatTile label="Auth Status" value={spf && dkim ? 'Secured' : (spf || dkim) ? 'Partial' : 'Not set'} tone={spf && dkim ? 'ok' : (spf || dkim) ? 'warn' : 'danger'} /></Col>
          </Row>
        </Col>
      </Row>

      {/* Row 2 — Auth cards + Inbox Placement */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} md={12}>
          <Section title="Email Authentication">

            {/* SPF */}
            <AuthCard
              label="SPF"
              passed={spf}
              summary={
                spf === true  ? 'Your domain is authorised to send via SES.' :
                spf === false ? 'SES identity not verified — SPF check will fail.' :
                                'Add a TXT record to your DNS to authorise SES.'
              }
            >
              <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.6 }}>
                SPF (Sender Policy Framework) tells receiving mail servers that SES is allowed to send email for your domain.
                Add this record in your <strong>DNS provider</strong> (Cloudflare, GoDaddy, Namecheap, Route 53, etc.):
              </p>
              <Step n={1} text="Log in to your DNS provider and open the DNS records for your domain." />
              <Step n={2} text='Add a new TXT record with the host/name set to "@" (or your domain root):' />
              <DnsRecord type="TXT" host={`@  (${domain})`} value="v=spf1 include:amazonses.com ~all" />
              <Step n={3} text="Save the record. DNS changes can take up to 48 hours to propagate, but usually under 1 hour." />
              <Step n={4} text={`Once done, re-verify your identity in Settings → SES Connection, then click Refresh on this page.`} />
              <Button
                size="small" icon={<ExportOutlined />}
                onClick={() => navigate('/settings#ses')}
                style={{ borderRadius: 6, marginTop: 4 }}
              >
                Go to SES Settings
              </Button>
              <DnsVerifyButton type="spf" domain={domain} />
            </AuthCard>

            {/* DKIM */}
            <AuthCard
              label="DKIM"
              passed={dkim}
              summary={
                dkim === true  ? 'Emails are cryptographically signed — great for deliverability.' :
                dkim === false ? 'DKIM not enabled. Add the CNAME records below to your DNS.' :
                                 'Connect SES to generate your DKIM keys.'
              }
            >
              <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.6 }}>
                DKIM (DomainKeys Identified Mail) cryptographically signs your emails so receivers can verify they weren't tampered with.
                AWS SES generates 3 CNAME records you must add to your DNS:
              </p>
              {dkimTokens.length > 0 ? (
                <>
                  <Step n={1} text="Add these 3 CNAME records in your DNS provider:" />
                  {dkimTokens.map(tok => (
                    <DnsRecord
                      key={tok}
                      type="CNAME"
                      host={`${tok}._domainkey.${domain}`}
                      value={`${tok}.dkim.amazonses.com`}
                    />
                  ))}
                  <Step n={2} text="Save the records. It can take up to 72 hours for AWS to detect them." />
                  <Step n={3} text="Come back and click Refresh — DKIM will show Pass once verified." />
                  <DnsVerifyButton type="dkim" domain={domain} tokens={dkimTokens} />
                </>
              ) : (
                <>
                  <Step n={1} text="Go to Settings → SES Connection and save your AWS credentials." />
                  <Step n={2} text="Once connected, return here — your 3 DKIM CNAME records will appear automatically." />
                  <Button
                    size="small" icon={<ExportOutlined />}
                    onClick={() => navigate('/settings#ses')}
                    style={{ borderRadius: 6, marginTop: 4 }}
                  >
                    Go to SES Settings
                  </Button>
                </>
              )}
            </AuthCard>

            {/* DMARC */}
            <AuthCard
              label="DMARC"
              passed={dmarcPassed ? true : null}
              summary={dmarcPassed ? 'DNS record verified — DMARC is active.' : 'Add a TXT record to your DNS, then click Verify DNS Record below.'}
            >
              <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.6 }}>
                DMARC tells receiving servers what to do when an email fails SPF or DKIM.
                Required by <strong>Gmail</strong> and <strong>Yahoo</strong> for bulk senders. Add this TXT record in your DNS:
              </p>
              <Step n={1} text='Add a new TXT record with host/name set to "_dmarc":' />
              <DnsRecord
                type="TXT"
                host={`_dmarc.${domain}`}
                value={`v=DMARC1; p=none; rua=mailto:dmarc@${domain}`}
              />
              <Step n={2} text='Start with p=none (monitor only). Once you confirm SPF and DKIM are passing, upgrade to p=quarantine or p=reject for stronger protection.' />
              <Step n={3} text={`Replace dmarc@${domain} with a real inbox you control — aggregate reports will be sent there.`} />
              <DnsVerifyButton type="dmarc" domain={domain} onVerified={markDmarcVerified} />
            </AuthCard>

            {/* BIMI */}
            <AuthCard
              label="BIMI"
              passed={null}
              summary="Displays your brand logo in Gmail — requires DMARC enforcement first."
            >
              <p style={{ fontSize: 12.5, color: 'var(--text-2)', margin: '0 0 12px', lineHeight: 1.6 }}>
                BIMI (Brand Indicators for Message Identification) shows your logo in Gmail inboxes. It's optional but builds trust.
              </p>
              <Step n={1} text="First complete SPF, DKIM, and DMARC with p=quarantine or p=reject." />
              <Step n={2} text="Host a square SVG logo at a public HTTPS URL (e.g. https://yourdomain.com/logo.svg)." />
              <Step n={3} text='Add a TXT record at "default._bimi.yourdomain.com":' />
              <DnsRecord
                type="TXT"
                host={`default._bimi.${domain}`}
                value={`v=BIMI1; l=https://${domain}/logo.svg`}
              />
              <Step n={4} text="For Gmail support, you also need a Verified Mark Certificate (VMC) from a certificate authority — this is an optional paid step." />
              <p style={{ fontSize: 11.5, color: 'var(--text-3)', margin: '8px 0 0', fontStyle: 'italic' }}>
                Note: BIMI cannot be auto-detected from our platform. Check your DNS records manually to confirm it's active.
              </p>
            </AuthCard>

          </Section>
        </Col>

        <Col xs={24} md={12}>
          <Section
            title="Inbox Placement (Estimated)"
            tooltip="Estimated from your sender score. Real placement data requires AWS SES Virtual Deliverability Manager."
          >
            <div style={{ paddingTop: 6 }}>
              {placement.map(({ provider, pct }) => (
                <div key={provider} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                    <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-2)' }}>{provider}</span>
                    <span style={{
                      fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600,
                      color: pct >= 80 ? 'var(--ok)' : pct >= 65 ? 'oklch(0.45 0.14 75)' : 'var(--danger)',
                    }}>~{pct}%</span>
                  </div>
                  <Progress
                    percent={pct} showInfo={false}
                    strokeColor={pct >= 80 ? 'var(--ok)' : pct >= 65 ? '#d97706' : 'var(--danger)'}
                    trailColor="var(--border)" strokeWidth={6} style={{ margin: 0 }}
                  />
                </div>
              ))}
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4, lineHeight: 1.5 }}>
                Estimates only. Fix authentication issues and keep bounce rate below 5% to improve your score.
              </div>
            </div>
          </Section>
        </Col>
      </Row>

      {/* Row 3 — Sending identities + Recommendations */}
      <Row gutter={[16, 16]}>
        {fromEmails.length > 0 && (
          <Col xs={24} md={12}>
            <Section title="Sending Identities">
              {fromEmails.map((e, i) => (
                <div key={e.id} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  padding: '10px 0',
                  borderBottom: i < fromEmails.length - 1 ? '1px solid var(--border)' : 'none',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <GlobalOutlined style={{ color: 'var(--text-3)', fontSize: 14 }} />
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text)' }}>
                        {e.email}
                        {e.is_default && <Tag style={{ marginLeft: 8, fontSize: 10, borderRadius: 4 }}>Default</Tag>}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{e.email.split('@')[1]}</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <Tag color={e.status === 'Success' ? 'success' : e.status === 'Pending' ? 'warning' : 'error'} style={{ fontSize: 10, borderRadius: 4 }}>
                      {e.status === 'Success' ? 'Verified' : e.status || 'Unknown'}
                    </Tag>
                    <Tag color={dkim ? 'success' : 'error'} style={{ fontSize: 10, borderRadius: 4 }}>DKIM</Tag>
                  </div>
                </div>
              ))}
            </Section>
          </Col>
        )}

        <Col xs={24} md={fromEmails.length > 0 ? 12 : 24}>
          <Section title="Recommendations">
            <div style={{ paddingTop: 6 }}>
              {recs.map((r, i) => (
                <Rec key={i} tone={r.tone} icon={r.icon} title={r.title} body={r.body} />
              ))}
            </div>
          </Section>
        </Col>
      </Row>
    </div>
  );
};

export default Deliverability;
